import { ChangeEvent, FormEvent, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Container, DialogContent, IconButton, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import type { SavedFilter } from '@/types';

import favoritesStyles from '../Favorites/Favorites.module.scss';
import { FormInput } from '../uiComponents/FormInput';
import { MyButton } from '../uiComponents/MyButton';
import { StyledDialog, StyleDialogTitle } from '../uiComponents/styled';

interface RenameSavedFilterDialogProps {
  isSubmitting: boolean;
  onClose: () => void;
  onRename: (savedFilter: SavedFilter, name: string) => void;
  open: boolean;
  savedFilter: SavedFilter | null;
}

interface RenameSavedFilterFormValues {
  name: string;
}

const validateSavedFilterName = (value: string) => {
  const name = value.trim();

  if (!name) {
    return { error: 'Name is required', name };
  }

  if (name.length < 3) {
    return { error: 'Name must be at least 3 characters', name };
  }

  if (name.length > 60) {
    return { error: 'Name must be at most 60 characters', name };
  }

  return { error: null, name };
};

export const RenameSavedFilterDialog = ({
  isSubmitting,
  onClose,
  onRename,
  open,
  savedFilter,
}: RenameSavedFilterDialogProps) => {
  const formMethods = useForm<RenameSavedFilterFormValues>({
    defaultValues: { name: '' },
  });
  const { clearErrors, reset, setError, watch } = formMethods;
  const nameValidation = validateSavedFilterName(watch('name'));
  const currentName = savedFilter?.name.trim() ?? '';
  const canSubmit =
    Boolean(savedFilter) &&
    !nameValidation.error &&
    nameValidation.name !== currentName &&
    !isSubmitting;

  useEffect(() => {
    reset({ name: open && savedFilter ? savedFilter.name : '' });
  }, [open, reset, savedFilter]);

  const resetAndClose = () => {
    if (isSubmitting) return;

    reset({ name: '' });
    onClose();
  };

  const handleNameChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const { error } = validateSavedFilterName(value);

    if (error) {
      setError('name', { message: error, type: 'validation' });
    } else {
      clearErrors('name');
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!savedFilter || isSubmitting) return;

    const { error, name } = validateSavedFilterName(watch('name'));

    if (error) {
      setError('name', { message: error, type: 'validation' });
      return;
    }

    if (name === currentName) return;

    onRename(savedFilter, name);
  };

  return (
    <FormProvider {...formMethods}>
      <StyledDialog
        aria-busy={isSubmitting}
        aria-describedby='rename-saved-filter-dialog-current-name'
        aria-labelledby='rename-saved-filter-dialog-title'
        disableEscapeKeyDown={isSubmitting}
        onClose={resetAndClose}
        open={open}
        width='600'
      >
        <Container component='main' style={{ width: '100%' }}>
          <StyleDialogTitle
            id='rename-saved-filter-dialog-title'
            sx={{ m: 0, color: '#2A3842', textAlign: 'center' }}
          >
            RENAME SAVED FILTER
            <IconButton
              aria-label='Close'
              disabled={isSubmitting}
              onClick={resetAndClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </StyleDialogTitle>
          <DialogContent>
            <Box component='form' onSubmit={handleSubmit}>
              <Typography
                id='rename-saved-filter-dialog-current-name'
                sx={{
                  color: '#1650FF',
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  overflowWrap: 'anywhere',
                  textAlign: 'center',
                }}
              >
                {currentName}
              </Typography>
              <Box>
                <FormInput
                  autoComplete='off'
                  autoFocus
                  fullWidth
                  inputProps={{ 'aria-label': 'Enter new name' }}
                  isDisable={isSubmitting}
                  label='Enter new name'
                  name='name'
                  onChange={handleNameChange}
                  placeholder='Enter new name'
                  uiVariant='blank'
                />
              </Box>
              <Box className={favoritesStyles.submitButtons}>
                <MyButton
                  data={{
                    buttonName: isSubmitting ? 'Saving...' : 'Save',
                    buttonType: 'submit',
                    customWidth: '226px',
                    variant: 'contained',
                  }}
                  disabled={!canSubmit}
                />
                <MyButton
                  data={{
                    buttonName: 'Cancel',
                    buttonType: 'button',
                    customWidth: '226px',
                    styleType: 'cancel',
                    variant: 'outlined',
                  }}
                  disabled={isSubmitting}
                  onClick={resetAndClose}
                />
              </Box>
            </Box>
          </DialogContent>
        </Container>
      </StyledDialog>
    </FormProvider>
  );
};
